// JSON-LD Structured Data for SEO & AEO (Answer Engine Optimization)

export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "The Pearl at Grand Harbour",
  "description": "Luxury residences at Grand Harbour, Grand Cayman—designed for connection, calm, and everyday ease by the water.",
  "url": window.location.origin,
  "logo": `${window.location.origin}/logo.svg`,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Grand Harbour",
    "addressRegion": "Grand Cayman",
    "addressCountry": "KY"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1-345-XXX-XXXX",
    "contactType": "Sales",
    "areaServed": "KY",
    "availableLanguage": "English"
  },
  "sameAs": [
    "https://facebook.com/thepearlgrandharbour",
    "https://instagram.com/thepearlgrandharbour"
  ]
});

export const getWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "The Pearl at Grand Harbour",
  "url": window.location.origin,
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${window.location.origin}/?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
});

export const getPlaceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Place",
  "name": "The Pearl at Grand Harbour",
  "description": "Lagoon-side residential community in Grand Harbour, Grand Cayman",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Grand Harbour",
    "addressRegion": "Grand Cayman",
    "addressCountry": "KY"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "19.2866",
    "longitude": "-81.3744"
  },
  "amenityFeature": [
    {
      "@type": "LocationFeatureSpecification",
      "name": "Lagoon Swimming Area"
    },
    {
      "@type": "LocationFeatureSpecification",
      "name": "Fitness Center"
    },
    {
      "@type": "LocationFeatureSpecification",
      "name": "Food Hall"
    }
  ]
});

export const getFAQSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Why Grand Harbour?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Grand Harbour offers immediate access to everyday essentials with grocery stores, gyms, cafés, and schools within walking distance, plus it's just minutes from Seven Mile Beach and Camana Bay."
      }
    },
    {
      "@type": "Question",
      "name": "Who is The Pearl for?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Pearl is designed for discerning residents who want a luxury address in Grand Harbour—where community, dining, wellness, and everyday convenience are moments from the front door. It also offers a compelling opportunity for investors seeking lifestyle-led rental appeal in a proven location."
      }
    },
    {
      "@type": "Question",
      "name": "What amenities are planned?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Pearl will feature a signature lagoon and pool, fitness center, indoor food hall with eight kitchen concepts, outdoor bar, flexible event space, and lush landscaping throughout the community."
      }
    },
    {
      "@type": "Question",
      "name": "What types of residences are available?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Approximately 74 residences ranging from 1-bedroom condos to 3-bedroom townhomes. Floorplans and pricing details are coming soon - register your interest to be notified first."
      }
    }
  ]
});

export const getRealEstateListingSchema = (unit: { name: string; type: string; description: string }) => ({
  "@context": "https://schema.org",
  "@type": "RealEstateListing",
  "name": unit.name,
  "description": unit.description,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Grand Harbour",
    "addressRegion": "Grand Cayman",
    "addressCountry": "KY"
  },
  "accommodationCategory": unit.type
});

export const getInvestPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "InvestmentOpportunity",
  "name": "The Pearl at Grand Harbour - Investment Opportunity",
  "description": "Lifestyle-led rental appeal and strong location fundamentals make The Pearl a compelling investment opportunity in Grand Harbour, Grand Cayman",
  "url": `${window.location.origin}/invest`,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Grand Harbour",
    "addressRegion": "Grand Cayman",
    "addressCountry": "KY"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "19.2866",
    "longitude": "-81.3744"
  },
  "offers": {
    "@type": "Offer",
    "category": "Real Estate Investment",
    "availability": "https://schema.org/PreOrder",
    "priceCurrency": "KYD"
  },
  "investmentType": "Real Estate",
  "location": {
    "@type": "Place",
    "name": "The Pearl at Grand Harbour",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Grand Harbour",
      "addressRegion": "Grand Cayman",
      "addressCountry": "KY"
    }
  }
});

export const getBreadcrumbSchema = (pathname?: string) => {
  const baseUrl = window.location.origin;
  const currentPath = pathname || window.location.pathname;
  
  const breadcrumbs: Array<{
    "@type": "ListItem";
    position: number;
    name: string;
    item: string;
  }> = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: baseUrl
    }
  ];

  // Map paths to breadcrumb names
  const pathMap: Record<string, string> = {
    '/residences': 'Residences',
    '/amenities': 'Amenities',
    '/commercial': 'Commercial',
    '/invest': 'Invest',
    '/about': 'About Us',
  };

  // Add current page breadcrumb if it's not the home page
  if (currentPath !== '/' && pathMap[currentPath]) {
    breadcrumbs.push({
      "@type": "ListItem",
      position: 2,
      name: pathMap[currentPath],
      item: `${baseUrl}${currentPath}`
    });
  }

  // Handle property detail pages
  if (currentPath.startsWith('/property/')) {
    breadcrumbs.push({
      "@type": "ListItem",
      position: 2,
      name: "Residences",
      item: `${baseUrl}/residences`
    });
    breadcrumbs.push({
      "@type": "ListItem",
      position: 3,
      name: "Property Details",
      item: `${baseUrl}${currentPath}`
    });
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs
  };
};
